import { AIResponse } from '../types/server-types';
import { IResponseBuffer } from '../types/internal-types';

/**
 * Implements IResponseBuffer to provide ordered delivery of responses
 */
export class OrderedResponseBuffer implements IResponseBuffer {
    private waitingResponsesBuffer: AIResponse[] = [];
    private nextExpectedOrder: number;
    private signalOnNextValue: (() => void) | null = null;

    constructor(startingOrder: number = 0) {
        this.nextExpectedOrder = startingOrder;
    }

    push(responses: AIResponse[]): void {
        this.waitingResponsesBuffer.push(...responses);
        this.waitingResponsesBuffer.sort((a, b) => a.order - b.order);

        // Signal any waiting generator
        if (this.signalOnNextValue) {
            this.signalOnNextValue();
            this.signalOnNextValue = null;
        }
    }

    async *responses(): AsyncGenerator<AIResponse, void, unknown> {
        while (true) {
            // Find next response matching expected order
            const nextResponseIndexInBuffer =
                this.waitingResponsesBuffer.findIndex(
                    aiResponse => aiResponse.order === this.nextExpectedOrder
                );

            if (nextResponseIndexInBuffer === -1) {
                // Nothing ready yet - wait for next push
                await new Promise<void>(resolve => {
                    this.signalOnNextValue = resolve;
                });

                continue;
            }

            // Remove response from waitingResponsesBuffer and yield it
            const [nextResponse] = this.waitingResponsesBuffer.splice(
                nextResponseIndexInBuffer,
                1
            );
            this.nextExpectedOrder++;

            yield nextResponse;
        }
    }

    clone(): OrderedResponseBuffer {
        const clonedBuffer = new OrderedResponseBuffer(this.nextExpectedOrder);
        clonedBuffer.waitingResponsesBuffer = [...this.waitingResponsesBuffer];
        return clonedBuffer;
    }
}
